import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import mergeAudioBlobs from '../utils/audio.util';
import { AudioFileDataWithoutBlob, addUpdateData, getAllData, getAudioBlob, removeData } from '../utils/indexedDB';

// Define recording status as enum for consistency
export enum RecordingStatus {
  IDLE = 'Idle',
  RECORDING = 'Recording',
  PAUSED = 'Paused',
  COMPLETED = 'Completed'
}

// Helper function to generate a unique recording ID
const generateRecordingId = (patientId: string, soapId: string): string => {
  return `${patientId || 'unknown'}:${soapId || 'unknown'}`;
};

// Interface for recording data
interface AudioRecording extends AudioFileDataWithoutBlob {
  patientId: string;
  patientName: string;
  Soapid: string;
  SoapType: string;
  status: RecordingStatus;
  recordingTime: number;
  oldTime: number;
}

// Technical refs for recording (not stored in IndexedDB)
interface RecordingTechnicals {
  mediaRecorder: MediaRecorder | null;
  audioChunks: Blob[];
  stream: MediaStream | null;
  startTime: number;
  accumulatedTime: number;
  timer: NodeJS.Timeout | null;
  cleanupFunction: (() => void) | null; // Separate field for cleanup
  lastUpdateTime: number;
}

// Define context methods
interface AudioContextType {
  // State access
  recordings: Record<string, AudioRecording>;
  activeRecordingId: string | null;

  // Core recording operations
  startRecording: (params: {
    patientId: string;
    patientName: string;
    soapId: string;
    soapType: string;
  }) => Promise<void>;

  pauseRecording: (recordingId?: string) => Promise<void>;
  resumeRecording: (recordingId: string) => Promise<void>;
  stopRecording: (recordingId?: string) => Promise<boolean>;
  deleteRecording: (recordingId: string) => Promise<void>;

  // Helpers
  formatTime: (seconds: number) => string;
  updateNoteType: (recordingId: string, noteType: string) => Promise<void>;
  getRecordingStatus: (patientId: string, soapId: string) => {
    status: RecordingStatus;
    time: number;
    isActive: boolean;
  };
}

// Create context with default values
const AudioRecordingContext = createContext<AudioContextType>({
  recordings: {},
  activeRecordingId: null,
  startRecording: async () => {},
  pauseRecording: async () => {},
  resumeRecording: async () => {},
  stopRecording: async () => false,
  deleteRecording: async () => {},
  formatTime: () => '00:00',
  updateNoteType: async () => {},
  getRecordingStatus: () => ({ status: RecordingStatus.IDLE, time: 0, isActive: false }),
});

export const AudioRecordingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Core state: dictionary of recordings and active recording ID
  const [recordings, setRecordings] = useState<Record<string, AudioRecording>>({});
  const [activeRecordingId, setActiveRecordingId] = useState<string | null>(null);

  // Technical refs stored in a separate dictionary
  const recordingTechnicals = useRef<Record<string, RecordingTechnicals>>({});

  // Operation locks to prevent race conditions
  const operationLocks = useRef<Record<string, boolean>>({
    isLoading: false,
    isStarting: false,
    isPausing: false,
    isResuming: false,
    isStopping: false,
  });

  // Load all recordings on mount and set up refresh timer
  useEffect(() => {
    loadRecordings();

    // Set up interval to refresh recordings list - reduced frequency for better performance
    const interval = setInterval(loadRecordings, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load recordings from IndexedDB with improved handling of active recordings
  const loadRecordings = React.useCallback(async () => {
    if (operationLocks.current.isLoading) return;

    try {
      operationLocks.current.isLoading = true;

      const allData = await getAllData();

      // Update state with better handling of active recordings
      setRecordings(prevRecordings => {
        const newRecordingsMap: Record<string, AudioRecording> = {};

        // Process all recordings from database
        allData.forEach(audio => {
          const recordingId = generateRecordingId(audio.patientId, audio.Soapid);

          // Check if this is the active recording
          const isActiveRecording = recordingId === activeRecordingId;
          const isRecordingInProgress = isActiveRecording &&
                                       prevRecordings[recordingId]?.status === RecordingStatus.RECORDING;

          // Create the recording object with correct time handling
          if (isRecordingInProgress) {
            // For active recordings, preserve ALL existing state data including time
            newRecordingsMap[recordingId] = {
              ...prevRecordings[recordingId]
            };
          } else {
            // For non-active recordings, get data from DB but preserve oldTime if needed
            const existingTime = prevRecordings[recordingId]?.recordingTime || 0;

            newRecordingsMap[recordingId] = {
              patientId: audio.patientId,
              patientName: audio.patientName,
              Soapid: audio.Soapid,
              SoapType: audio.SoapType,
              status: audio.status as RecordingStatus,
              oldTime: audio.oldTime || 0,
              // Use the greater of the two times to prevent jumps backward
              recordingTime: Math.max(existingTime, audio.recordingTime || 0)
            };
          }
        });

        // Important: For any active recording that's not in the DB yet,
        // make sure to preserve it in the state
        if (activeRecordingId && prevRecordings[activeRecordingId]) {
          if (!newRecordingsMap[activeRecordingId]) {
            newRecordingsMap[activeRecordingId] = prevRecordings[activeRecordingId];
          }
        }

        // Only update state if changed (shallow compare keys and values)
        const prevKeys = Object.keys(prevRecordings);
        const newKeys = Object.keys(newRecordingsMap);
        if (prevKeys.length !== newKeys.length) return newRecordingsMap;
        for (const key of newKeys) {
          const prev = prevRecordings[key];
          const next = newRecordingsMap[key];
          if (!prev || !next) return newRecordingsMap;
          // Compare all fields shallowly
          for (const field of Object.keys(next)) {
            if (prev[field as keyof AudioRecording] !== next[field as keyof AudioRecording]) return newRecordingsMap;
          }
        }
        // No changes, return previous state to avoid re-render
        return prevRecordings;
      });
    } catch (error) {
      console.error('Failed to load recordings:', error);
    } finally {
      operationLocks.current.isLoading = false;
    }
  }, [activeRecordingId]);

  // Format time for display (mm:ss)
  const formatTime = React.useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }, []);

  // Get recording status for a specific patient/case
  const getRecordingStatus = React.useCallback((patientId: string, soapId: string) => {
    const recordingId = generateRecordingId(patientId, soapId);
    const recording = recordings[recordingId];
    const isActive = activeRecordingId === recordingId;

    if (!recording) {
      return {
        status: RecordingStatus.IDLE,
        time: 0,
        isActive: false
      };
    }

    return {
      status: recording.status,
      time: recording.recordingTime || recording.oldTime || 0,
      isActive
    };
  }, [recordings, activeRecordingId]);




  // Start a timer for a recording - improved implementation with tab visibility handling
  const startTimer = (recordingId: string) => {
    const technicals = recordingTechnicals.current[recordingId];
    if (!technicals) return;

    // Clear any existing timer to prevent duplicates
    if (technicals.timer) {
      clearInterval(technicals.timer);
      technicals.timer = null;
    }

    // Execute cleanup function if it exists
    if (technicals.cleanupFunction) {
      technicals.cleanupFunction();
      technicals.cleanupFunction = null;
    }

    // Set start time and last update time precisely
    const now = Date.now();
    technicals.startTime = now;
    technicals.lastUpdateTime = now;

    // Use a variable to track if the page is visible
    let isPageVisible = !document.hidden;

    // Track the time when the page became hidden
    let pageHiddenTime = 0;

    // Add page visibility event listeners
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is now hidden
        isPageVisible = false;
        pageHiddenTime = Date.now();
      } else {
        // Page is now visible again - calculate time passed while hidden
        isPageVisible = true;
        if (pageHiddenTime > 0) {
          const hiddenDuration = (Date.now() - pageHiddenTime) / 1000;

          // Add the hidden time to our accumulated time
          technicals.accumulatedTime += hiddenDuration;

          // Update last update time to now
          technicals.lastUpdateTime = Date.now();

          // Update UI immediately to show the correct time
          setRecordings(prev => {
            if (!prev[recordingId]) return prev;

            return {
              ...prev,
              [recordingId]: {
                ...prev[recordingId],
                recordingTime: Math.floor(technicals.accumulatedTime)
              }
            };
          });
        }
      }
    };

    // Add the event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Create new timer with reduced frequency
    technicals.timer = setInterval(() => {
      // Only update time when page is visible
      if (isPageVisible) {
        const now = Date.now();

        // Calculate elapsed time since last update
        const elapsedSinceLastUpdate = (now - technicals.lastUpdateTime) / 1000;
        technicals.lastUpdateTime = now;

        // Guard against negative or very large time jumps
        if (elapsedSinceLastUpdate < 0 || elapsedSinceLastUpdate > 5) {
          return;
        }

        // Update accumulated time
        technicals.accumulatedTime += elapsedSinceLastUpdate;

        // Update state with the new time
        setRecordings(prev => {
          // Early return if recording no longer exists
          if (!prev[recordingId]) return prev;

          // Only update if time has meaningfully changed
          const currentSeconds = Math.floor(technicals.accumulatedTime);
          const prevSeconds = Math.floor(prev[recordingId].recordingTime || 0);

          if (currentSeconds === prevSeconds) return prev;

          // Create a new recordings object with updated time
          return {
            ...prev,
            [recordingId]: {
              ...prev[recordingId],
              recordingTime: currentSeconds
            }
          };
        });
      }
    }, 500); // 500ms is a good balance between smoothness and performance

    // Store the cleanup function separately
    technicals.cleanupFunction = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (technicals.timer) {
        clearInterval(technicals.timer);
        technicals.timer = null;
      }
    };
  };

  // Stop a timer for a recording
  const stopTimer = (recordingId: string) => {
    const technicals = recordingTechnicals.current[recordingId];
    if (!technicals) return 0;

    // Calculate final elapsed time
    const now = Date.now();
    const elapsedSinceLastUpdate = (now - technicals.lastUpdateTime) / 1000;

    // Update accumulated time one last time
    technicals.accumulatedTime += elapsedSinceLastUpdate;

    // Clear timer
    if (technicals.timer) {
      clearInterval(technicals.timer);
      technicals.timer = null;
    }

    // Execute cleanup function if it exists
    if (technicals.cleanupFunction) {
      technicals.cleanupFunction();
      technicals.cleanupFunction = null;
    }

    // Update state one final time
    setRecordings(prev => {
      if (!prev[recordingId]) return prev;

      return {
        ...prev,
        [recordingId]: {
          ...prev[recordingId],
          recordingTime: Math.floor(technicals.accumulatedTime)
        }
      };
    });

    return Math.floor(technicals.accumulatedTime);
  };

// Pause a recording
const pauseRecording = React.useCallback(async (recordingId?: string) => {
  if (operationLocks.current.isPausing) return;

  try {
    operationLocks.current.isPausing = true;

    // If no recording ID is provided, use the active one
    const targetId = recordingId || activeRecordingId;
    if (!targetId) return;

    const recording = recordings[targetId];
    if (!recording || recording.status !== RecordingStatus.RECORDING) return;

    const technicals = recordingTechnicals.current[targetId];
    if (!technicals || !technicals.mediaRecorder) return;

    // Stop the timer first to get accurate time
    const totalTime = stopTimer(targetId);

    // Stop the recorder
    if (technicals.mediaRecorder.state !== 'inactive') {
      technicals.mediaRecorder.stop();
    }

    // Wait for final data and save
    await new Promise<void>(resolve => {
      if (technicals.mediaRecorder) {
        technicals.mediaRecorder.onstop = async () => {
          try {
            // Create blob from chunks
            if (technicals.audioChunks.length === 0) {
              console.warn('No audio chunks recorded');
              resolve();
              return;
            }

            const audioBlob = new Blob(technicals.audioChunks, {
              type: 'audio/webm;codecs=opus'
            });

            // Get previous blob if any
            let mergedBlob: Blob;
            try {
              const prevBlob = await getAudioBlob(recording);

              if (prevBlob && prevBlob.size > 0) {
                mergedBlob = await mergeAudioBlobs([prevBlob, audioBlob]);
              } else {
                mergedBlob = audioBlob;
              }
            } catch (error) {
              console.error('Error getting/merging previous blob:', error);
              mergedBlob = audioBlob;
            }

            // Update recording in state
            const updatedRecording = {
              ...recording,
              status: RecordingStatus.PAUSED,
              oldTime: totalTime,
              recordingTime: totalTime
            };

            // Update IndexedDB
            await addUpdateData({
              ...updatedRecording,
              audio: mergedBlob
            });

            // Update state
            setRecordings(prev => ({
              ...prev,
              [targetId]: updatedRecording
            }));

            // Clear active recording if this was the active one
            if (activeRecordingId === targetId) {
              setActiveRecordingId(null);
            }

            // Clear chunks for next segment
            technicals.audioChunks = [];

            resolve();
          } catch (error) {
            console.error('Error during pause operation:', error);
            resolve();
          } finally {
            // Stop all tracks in the stream
            if (technicals.stream) {
              technicals.stream.getTracks().forEach(track => track.stop());
            }

            // Remove media recorder
            technicals.mediaRecorder = null;
            technicals.stream = null;
          }
        };
      } else {
        resolve();
      }
    });

  } catch (error) {
    console.error('Failed to pause recording:', error);
  } finally {
    operationLocks.current.isPausing = false;
  }
}, [activeRecordingId, recordings]);


// Fix to prevent audio cutoffs when pausing and resuming recordings

// Inside the AudioRecordingProvider component
// Modify the startRecording function like this:

const startRecording = React.useCallback(async (params: {
  patientId: string;
  patientName: string;
  soapId: string;
  soapType: string;
}) => {
  if (operationLocks.current.isStarting) return;

  try {
    operationLocks.current.isStarting = true;

    const { patientId, patientName, soapId, soapType } = params;
    const recordingId = generateRecordingId(patientId, soapId);

    // Check if we're already at the limit of 3 recordings
    if (Object.keys(recordings).length >= 3 && !recordings[recordingId]) {
      toast.error('You can only have 3 recordings in progress at a time');
      return;
    }

    // First, pause any active recording
    if (activeRecordingId && activeRecordingId !== recordingId) {
      const activeRecording = recordings[activeRecordingId];
      if (activeRecording && activeRecording.status === RecordingStatus.RECORDING) {
        console.log("Auto-pausing current recording before starting new one");
        await pauseRecording(activeRecordingId);

        // Add a small delay to ensure the pause is completed
        await new Promise(resolve => setTimeout(resolve, 300));

        // Show a toast to inform the user
        toast.info(`Recording for ${activeRecording.patientName} has been paused`, {
          position: "top-right",
          autoClose: 3000
        });
      }
    }

    // Check permissions and get stream
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Create a new MediaRecorder instance
    const recorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    // Determine if this is a new recording or resuming
    const existingRecording = recordings[recordingId];
    const initialTime = existingRecording ? (existingRecording.recordingTime || existingRecording.oldTime || 0) : 0;

    // Set up technicals for this recording with improved timing values
    recordingTechnicals.current[recordingId] = {
      mediaRecorder: recorder,
      audioChunks: [],
      stream,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
      accumulatedTime: initialTime,
      timer: null,
      cleanupFunction: null
    };

    // Set up data handler for the recorder
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        const technicals = recordingTechnicals.current[recordingId];
        if (technicals) {
          technicals.audioChunks.push(e.data);
        }
      }
    };

    // Create or update recording in state
    const newRecording: AudioRecording = {
      patientId,
      patientName,
      Soapid: soapId,
      SoapType: soapType,
      status: RecordingStatus.RECORDING,
      oldTime: initialTime,
      recordingTime: initialTime
    };

    // Start the recorder
    recorder.start(1000); // Collect data every second

    // Update state
    setRecordings(prev => ({
      ...prev,
      [recordingId]: newRecording
    }));

    setActiveRecordingId(recordingId);

    // Start timer immediately after state updates
    setTimeout(() => {
      startTimer(recordingId);
    }, 0);

    // FIXED: Don't overwrite existing audio when resuming
    if (existingRecording && existingRecording.status === RecordingStatus.PAUSED) {
      // If resuming, just update the status but preserve the audio blob
      console.log('Resuming existing recording - preserving audio data');

      // Get existing audio blob
      const existingBlob = await getAudioBlob(existingRecording);

      // Only update status and metadata, keep the existing audio
      if (existingBlob && existingBlob.size > 0) {
        console.log(`Preserving existing audio blob: ${existingBlob.size} bytes`);
        await addUpdateData({
          ...newRecording,
          audio: existingBlob
        });
      } else {
        console.warn('No existing audio blob found when resuming recording');
        await addUpdateData({
          ...newRecording,
          audio: new Blob([], { type: 'audio/webm' })
        });
      }
    } else {
      // For new recordings, start with an empty blob
      console.log('Starting new recording with empty blob');
      await addUpdateData({
        ...newRecording,
        audio: new Blob([], { type: 'audio/webm' })
      });
    }

  } catch (error) {
    console.error('Failed to start recording:', error);
    toast.error('Failed to start recording. Please check your microphone permissions.');
  } finally {
    operationLocks.current.isStarting = false;
  }
}, [recordings, activeRecordingId, pauseRecording]);

  // Resume a paused recording
  const resumeRecording = React.useCallback(async (recordingId: string) => {
    if (operationLocks.current.isResuming) return;

    try {
      operationLocks.current.isResuming = true;

      const recording = recordings[recordingId];
      if (!recording || recording.status !== RecordingStatus.PAUSED) return;

      // Check if any recording is active
      if (activeRecordingId && activeRecordingId !== recordingId) {
        // Get the active recording
        const activeRecording = recordings[activeRecordingId];

        // If active recording is actually recording, pause it first
        if (activeRecording && activeRecording.status === RecordingStatus.RECORDING) {
          console.log("Auto-pausing active recording before resuming another");
          await pauseRecording(activeRecordingId);

          // Add a small delay to ensure the pause is completed
          await new Promise(resolve => setTimeout(resolve, 300));

          // Show a toast to inform the user
          toast.info(`Recording for ${activeRecording.patientName} has been paused`, {
            position: "top-right",
            autoClose: 3000
          });
        }
      }

      // Now start this recording using existing data
      await startRecording({
        patientId: recording.patientId,
        patientName: recording.patientName,
        soapId: recording.Soapid,
        soapType: recording.SoapType
      });

    } catch (error) {
      console.error('Failed to resume recording:', error);
    } finally {
      operationLocks.current.isResuming = false;
    }
  }, [activeRecordingId, recordings, pauseRecording, startRecording]);

  // Stop a recording and prepare for upload
  const stopRecording = React.useCallback(async (recordingId?: string): Promise<boolean> => {
    if (operationLocks.current.isStopping) return false;

    try {
      operationLocks.current.isStopping = true;

      // If no recording ID is provided, use the active one
      const targetId = recordingId || activeRecordingId;
      if (!targetId) return false;

      const recording = recordings[targetId];
      if (!recording) return false;

      // If recording is active, pause it first
      if (recording.status === RecordingStatus.RECORDING) {
        await pauseRecording(targetId);
      }

      // Check minimum recording time (3 minutes)
      const recordingTime = recording.recordingTime || recording.oldTime || 0;
      if (recordingTime < 180 ) {
        const toastId = "min-recording-time";
        if (!toast.isActive(toastId)) {
          toast.error('Recording is less than 3 minutes so cannot upload..!!!', { toastId });
        }
        return false;
      }



      // Mark as completed
      const completedRecording = {
        ...recording,
        status: RecordingStatus.COMPLETED
      };

      // Save to IndexedDB
      await addUpdateData({
        ...completedRecording,
        audio: await getAudioBlob(recording) || new Blob([], { type: 'audio/webm' })
      });

      // Update state
      setRecordings(prev => ({
        ...prev,
        [targetId]: completedRecording
      }));

      // If this was the active recording, clear it
      if (activeRecordingId === targetId) {
        setActiveRecordingId(null);
      }

      return true;
    } catch (error) {
      console.error('Failed to stop recording:', error);
      return false;
    } finally {
      operationLocks.current.isStopping = false;
    }
  }, [activeRecordingId, recordings, pauseRecording]);

  // Delete a recording
  const deleteRecording = React.useCallback(async (recordingId: string): Promise<void> => {
    if (!recordings[recordingId]) return;

    try {
      const recording = recordings[recordingId];

      // If this is the active recording, stop it first
      if (activeRecordingId === recordingId) {
        const technicals = recordingTechnicals.current[recordingId];

        // Stop timer
        if (technicals) {
          if (technicals.timer) {
            clearInterval(technicals.timer);
            technicals.timer = null;
          }

          if (technicals.cleanupFunction) {
            technicals.cleanupFunction();
            technicals.cleanupFunction = null;
          }
        }

        // Stop media recorder if it's running
        if (technicals && technicals.mediaRecorder && technicals.mediaRecorder.state !== 'inactive') {
          technicals.mediaRecorder.stop();
        }

        // Stop stream tracks
        if (technicals && technicals.stream) {
          technicals.stream.getTracks().forEach(track => track.stop());
        }

        // Clear active recording
        setActiveRecordingId(null);
      }

      // Remove from IndexedDB
      await removeData(recording);

      // Remove from state
      setRecordings(prev => {
        const updated = { ...prev };
        delete updated[recordingId];
        return updated;
      });

      // Clean up technicals
      if (recordingTechnicals.current[recordingId]) {
        delete recordingTechnicals.current[recordingId];
      }

    } catch (error) {
      console.error('Failed to delete recording:', error);
    }
  }, [activeRecordingId, recordings]);

  // Update note type for a recording
  const updateNoteType = React.useCallback(async (recordingId: string, noteType: string): Promise<void> => {
    if (!recordings[recordingId]) return;

    try {
      const recording = recordings[recordingId];

      // Update recording with new note type
      const updatedRecording = {
        ...recording,
        SoapType: noteType
      };

      // Save to IndexedDB
      await addUpdateData({
        ...updatedRecording,
        audio: await getAudioBlob(recording) || new Blob([], { type: 'audio/webm' })
      });

      // Update state
      setRecordings(prev => ({
        ...prev,
        [recordingId]: updatedRecording
      }));

    } catch (error) {
      console.error('Failed to update note type:', error);
    }
  }, [recordings]);

  // Context value
  const contextValue: AudioContextType = React.useMemo(() => ({
    recordings,
    activeRecordingId,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    deleteRecording,
    formatTime,
    updateNoteType,
    getRecordingStatus
  }), [recordings, activeRecordingId, startRecording, pauseRecording, resumeRecording, stopRecording, deleteRecording, formatTime, updateNoteType, getRecordingStatus]);

  return (
    <AudioRecordingContext.Provider value={contextValue}>
      {children}
    </AudioRecordingContext.Provider>
  );
};

// Custom hook for using the context
export const useAudioRecording = () => useContext(AudioRecordingContext);
